import type { CreateIntegrationOnboardingSessionResponse } from '@heidi/integrations/connected-apps/contracts';

/**
 * The editor experience the backend has RESOLVED for an Athena onboarding
 * session.
 *
 * Wire name is `template_editor_version` (html/json/markdown); internally we
 * say `EditorVersion` because the FE analytics property of that name already
 * means template-builder generation v1/v2 — the analytics dimension for this
 * value is `sectional_editor_version` (AME-999 naming decision).
 */
export type EditorVersion = 'html' | 'json' | 'markdown';

/**
 * Tagged read of the backend's resolved decision. `unpinned` (null/absent —
 * a pre-#18956 backend or stale cache) and `unrecognized` (corrupt value)
 * BOTH fail closed to html at the resolver; they are distinct variants on
 * purpose so the corrupt case can be warned while the null case stays quiet.
 * Neither ever falls back to a client-side flag re-derivation — that path no
 * longer exists.
 */
export type StoredEditorVersion =
  | { kind: 'pinned'; version: EditorVersion }
  | { kind: 'unpinned' }
  | { kind: 'unrecognized'; raw: unknown };

// kb:{ceiling}: the generated FE type (`CreateIntegrationOnboardingSessionResponse`)
// lacks `template_editor_version` until the schema regen picks up the backend
// field (ml-scribe #18956), but the response decoder spreads extra JSON keys
// through, so the value arrives at runtime the moment the backend deploys.
// Typed pick off the decoded session; generated files are never hand-edited.
export const readStoredEditorVersion = (
  session?: CreateIntegrationOnboardingSessionResponse
): StoredEditorVersion => {
  const raw = (session as { template_editor_version?: unknown } | undefined)
    ?.template_editor_version;
  if (raw === null || raw === undefined) return { kind: 'unpinned' };
  if (raw === 'html' || raw === 'json' || raw === 'markdown') {
    return { kind: 'pinned', version: raw };
  }
  return { kind: 'unrecognized', raw };
};
